import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingNew = () => {
  const { isAuthenticated, user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discountCode, setDiscountCode] = useState('');
  const [discountResult, setDiscountResult] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/plans/all`);
      setPlans(response.data.plans);
    } catch (error) {
      // Silent — UI will render an empty state and reload button
    } finally {
      setLoading(false);
    }
  };

  const handleValidateDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountError('');
    setDiscountResult(null);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/discount-codes/validate`, { code: discountCode });
      setDiscountResult(response.data);
    } catch (err) {
      setDiscountError(err.response?.data?.detail || 'Codigo invalido');
    }
  };

  const handleRedeemDiscount = async () => {
    if (!discountCode.trim() || !isAuthenticated) return;
    setRedeeming(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/discount-codes/redeem`, 
        { code: discountCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiscountResult(null);
      alert(response.data.message);
      window.location.href = '/dashboard';
    } catch (err) {
      setDiscountError(err.response?.data?.detail || 'Error al canjear codigo');
    } finally {
      setRedeeming(false);
    }
  };

  const plansOrder = ['free', 'basic', 'standard', 'professional', 'enterprise'];

  const handlePurchasePlan = async (planKey, paymentMethod = 'stripe') => {
    if (!isAuthenticated) {
      window.location.href = '/register';
      return;
    }

    try {
      const origin = window.location.origin;
      const token = localStorage.getItem('token');
      
      if (paymentMethod === 'paypal') {
        // PayPal flow
        const response = await axios.post(
          `${BACKEND_URL}/api/payments/paypal/create-order`,
          {
            package_id: planKey,
            origin_url: origin,
            request_type: 'plan'
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.approve_url) {
          window.location.href = response.data.approve_url;
        }
      } else {
        // Stripe flow
        const response = await axios.post(
          `${BACKEND_URL}/api/stripe/create-checkout`,
          {
            package_id: planKey,
            package_type: 'plan',
            origin_url: origin
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.url) {
          window.location.href = response.data.url;
        }
      }
    } catch (error) {
      alert('Error al procesar el pago. Por favor intenta de nuevo.');
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'Personalizado';
    return price === 0 ? 'Gratis' : `$${price.toFixed(2)}`;
  };

  const getPeriodText = (period, durationDays) => {
    if (period === 'trial') return `(${durationDays} días)`;
    if (period === 'monthly') return '/mes';
    if (period === 'annual') return '/año';
    return '';
  };

  if (loading) {
    return (
      <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
        <div className="px-4 sm:px-10 md:px-20 lg:px-40">
          <Header />
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-300">Cargando planes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!plans) {
    return (
      <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
        <div className="px-4 sm:px-10 md:px-20 lg:px-40">
          <Header />
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">Error al cargar los planes. Por favor, intenta de nuevo más tarde.</p>
              <button 
                onClick={loadPlans}
                className="px-6 py-2 bg-primary text-white rounded-lg font-bold hover:bg-primary/90"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Header />
        
        <div className="py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-black text-gray-900 dark:text-white mb-4">
              Elige el Plan Perfecto para Ti
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Comienza gratis o elige el plan que se adapte a tus necesidades
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
            {plans && plansOrder.map((planKey) => {
              const plan = plans[planKey];
              if (!plan) return null;
              
              const isHighlighted = planKey === 'standard';
              
              return (
                <div
                  key={planKey}
                  className={`rounded-xl p-6 flex flex-col gap-4 shadow-sm ${
                    isHighlighted
                      ? 'bg-primary text-white ring-2 ring-primary/50 transform scale-105'
                      : 'bg-white dark:bg-[#1a2530] border border-gray-200 dark:border-[#283039]'
                  }`}
                >
                  <h3 className={`text-lg font-bold ${
                    isHighlighted ? 'text-white' : 'text-gray-900 dark:text-white'
                  }`}>
                    {plan.name}
                  </h3>
                  
                  <p className={`text-3xl font-black ${
                    isHighlighted ? 'text-white' : 'text-gray-900 dark:text-white'
                  }`}>
                    {formatPrice(plan.price)}
                    <span className={`text-sm font-medium ${
                      isHighlighted ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {getPeriodText(plan.period, plan.duration_days)}
                    </span>
                  </p>
                  
                  <div className={`text-sm font-semibold mb-2 ${
                    isHighlighted ? 'text-white' : 'text-primary'
                  }`}>
                    {plan.credits ? `${plan.credits.toLocaleString()} créditos` : 'Créditos según acuerdo'}
                  </div>
                  
                  <ul className="space-y-2 text-sm flex-grow">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className={`text-sm mt-0.5 ${
                          isHighlighted ? 'text-white' : 'text-green-500'
                        }`}>
                          ✅
                        </span>
                        <span className={isHighlighted ? 'text-white' : 'text-gray-600 dark:text-gray-300'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  
                  {plan.credit_refill_price && (
                    <div className={`text-xs mt-2 p-2 rounded ${
                      isHighlighted ? 'bg-white/20' : 'bg-gray-100 dark:bg-[#283039]'
                    }`}>
                      <p className={isHighlighted ? 'text-white' : 'text-gray-700 dark:text-gray-300'}>
                        Recarga: ${plan.credit_refill_price.toFixed(2)} por 1,000 créditos
                      </p>
                    </div>
                  )}
                  
                  {planKey === 'free' ? (
                    <Link
                      to="/register"
                      className={`w-full mt-4 flex items-center justify-center rounded-lg h-10 px-4 font-bold ${
                        isHighlighted
                          ? 'bg-white text-primary hover:bg-gray-100'
                          : 'bg-primary/20 text-primary hover:bg-primary/30'
                      } transition-colors`}
                    >
                      Probar Gratis
                    </Link>
                  ) : planKey === 'enterprise' ? (
                    <a
                      href="mailto:contact@gpt-jdp.com?subject=Consulta%20Plan%20Enterprise%20GPT-JDP&body=Hola%2C%20me%20interesa%20conocer%20las%20opciones%20del%20Plan%20Enterprise%20para%20mi%20empresa."
                      data-testid={`enterprise-contact-btn-${planKey}`}
                      className={`w-full mt-4 flex items-center justify-center rounded-lg h-10 px-4 font-bold ${
                        isHighlighted
                          ? 'bg-white text-primary hover:bg-gray-100'
                          : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                      } transition-colors`}
                    >
                      Solicitar Cotización
                    </a>
                  ) : (
                    <div className="mt-4 space-y-2">
                      <button
                        data-testid={`paypal-checkout-btn-${planKey}`}
                        onClick={() => {
                          if (!isAuthenticated) {
                            window.location.href = '/register';
                          } else {
                            handlePurchasePlan(planKey, 'paypal');
                          }
                        }}
                        className={`w-full flex items-center justify-center rounded-lg h-10 px-4 font-bold ${
                          isHighlighted
                            ? 'bg-[#0070BA] text-white hover:bg-[#005EA6]'
                            : 'bg-[#0070BA] text-white hover:bg-[#005EA6]'
                        } transition-colors`}
                      >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 00-.794.68l-.04.22-.63 3.993-.028.15a.805.805 0 01-.794.68H7.72a.483.483 0 01-.477-.558L7.418 21h1.518l.95-6.02h1.385c4.678 0 7.75-2.203 8.796-6.502z"/>
                          <path d="M2.379 0C1.84 0 1.3.447 1.187 1.006L0 8.095c-.113.558.26 1.006.798 1.006h2.24L7.45 2.32A1.417 1.417 0 018.835 1.32h8.028c.3 0 .574.122.77.32.282.282.414.674.33 1.073l-.58 2.93c.81.38 1.474.952 1.905 1.685 1.216-3.933-.49-5.87-2.573-6.593A7.374 7.374 0 0014.728 0H2.38z"/>
                        </svg>
                        Pagar con PayPal
                      </button>
                      <p className={`text-xs text-center ${isHighlighted ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                        Acepta tarjeta de crédito, débito y saldo PayPal
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="max-w-5xl mx-auto bg-white dark:bg-[#1a2530] rounded-xl p-8 mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              Tabla de Consumo de Créditos
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Tarea de IA</th>
                    <th className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">Créditos</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300">Texto mejorado (500 palabras)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">10</td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300">Generación de Imagen (Estándar)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">50</td>
                  </tr>
                  
                  <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                    <td colSpan="2" className="py-2 px-4 text-sm font-bold text-gray-900 dark:text-white bg-gray-100 dark:bg-[#283039]">
                      VOZ Y AUDIO
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 pl-8">• Text-to-Speech (1 minuto)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">75</td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 pl-8">• Speech-to-Text (1 minuto)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">75</td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 pl-8">• Clonación de Voz</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">150</td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 pl-8">• Generación de Audio (1 minuto)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">75</td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 pl-8">• Edición de Audio</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">50</td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 pl-8">• Generación de Música (1 minuto)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">100</td>
                  </tr>
                  
                  <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                    <td colSpan="2" className="py-2 px-4 text-sm font-bold text-gray-900 dark:text-white bg-gray-100 dark:bg-[#283039]">
                      VIDEO
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 pl-8">• Generación de Video (10 segundos)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">500</td>
                  </tr>
                  
                  <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                    <td colSpan="2" className="py-2 px-4 text-sm font-bold text-gray-900 dark:text-white bg-gray-100 dark:bg-[#283039]">
                      CHATBOT AUTOMATIZADO (3 Niveles)
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 pl-8">• Chatbot Básico (Respuestas rápidas, 3 idiomas)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">5</td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 pl-8">• Chatbot Intermedio (Análisis moderado, 4 idiomas)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">15</td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 pl-8">• Chatbot Avanzado (Análisis profundo, 5 idiomas, código certificado)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">30</td>
                  </tr>
                  
                  <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                    <td colSpan="2" className="py-2 px-4 text-sm font-bold text-gray-900 dark:text-white bg-gray-100 dark:bg-[#283039]">
                      MÓDULOS ESPECIALIZADOS
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300">Ciberseguridad Básica (Análisis de seguridad)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">25</td>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-[#283039]">
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300">Contabilidad Básica (Consulta contable)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">15</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300">Asesoría Legal Básica (Consulta legal)</td>
                    <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-semibold">20</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Discount Code Section */}
          <div className="mt-12 mb-8" data-testid="discount-code-section">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 sm:p-8 text-white">
              <div className="max-w-2xl mx-auto text-center">
                <h3 className="text-xl sm:text-2xl font-bold mb-2">Tienes un codigo de descuento?</h3>
                <p className="text-sm opacity-90 mb-5">Ingresa tu codigo promocional para obtener descuentos exclusivos en el Plan Estandar</p>
                <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={e => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError(''); setDiscountResult(null); }}
                    placeholder="Ingresa tu codigo"
                    className="flex-1 px-4 py-3 rounded-lg text-gray-900 font-medium placeholder-gray-400 border-0 focus:ring-2 focus:ring-white"
                    data-testid="discount-code-input"
                  />
                  <button onClick={handleValidateDiscount} data-testid="validate-discount-btn"
                    className="px-6 py-3 bg-white text-green-700 rounded-lg font-bold hover:bg-gray-100 transition-colors">
                    Validar
                  </button>
                </div>
                {discountError && <p className="mt-3 text-red-200 font-medium text-sm">{discountError}</p>}
                {discountResult && (
                  <div className="mt-4 bg-white/10 backdrop-blur rounded-xl p-4 text-left">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <p className="font-bold text-lg">{discountResult.discount_percent}% de descuento</p>
                        <p className="text-sm opacity-90">{discountResult.plan_name} - ${discountResult.discounted_price}/mes</p>
                        <p className="text-sm opacity-80">{discountResult.credits?.toLocaleString()} creditos | {discountResult.duration_months} meses</p>
                        <p className="text-xs opacity-70 mt-1">{discountResult.spots_remaining} lugares disponibles de {discountResult.max_people}</p>
                      </div>
                      {isAuthenticated ? (
                        <button onClick={handleRedeemDiscount} disabled={redeeming} data-testid="redeem-discount-btn"
                          className="px-5 py-2.5 bg-white text-green-700 rounded-lg font-bold hover:bg-gray-100 disabled:opacity-50">
                          {redeeming ? 'Canjeando...' : 'Canjear Codigo'}
                        </button>
                      ) : (
                        <Link to="/register" className="px-5 py-2.5 bg-white text-green-700 rounded-lg font-bold hover:bg-gray-100 inline-block">
                          Registrate para canjear
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
              Preguntas Frecuentes
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <details className="bg-white dark:bg-[#1a2530] p-4 rounded-lg">
                <summary className="font-semibold text-gray-900 dark:text-white cursor-pointer">
                  ¿Puedo cambiar de plan en cualquier momento?
                </summary>
                <p className="mt-2 text-gray-600 dark:text-gray-300">
                  Sí, puedes actualizar o cambiar tu plan en cualquier momento desde tu dashboard.
                </p>
              </details>
              <details className="bg-white dark:bg-[#1a2530] p-4 rounded-lg">
                <summary className="font-semibold text-gray-900 dark:text-white cursor-pointer">
                  ¿Qué pasa si agoto mis créditos?
                </summary>
                <p className="mt-2 text-gray-600 dark:text-gray-300">
                  Puedes comprar créditos adicionales o actualizar a un plan superior. Los precios de recarga varían según tu plan actual.
                </p>
              </details>
              <details className="bg-white dark:bg-[#1a2530] p-4 rounded-lg">
                <summary className="font-semibold text-gray-900 dark:text-white cursor-pointer">
                  ¿Los créditos no utilizados se acumulan?
                </summary>
                <p className="mt-2 text-gray-600 dark:text-gray-300">
                  Los créditos de tu plan se renuevan mensualmente. Los créditos comprados por separado no caducan.
                </p>
              </details>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    </div>
  );
};

export default PricingNew;